/**
 * This file is part of ONP.
 *
 * Copyright (c) Opensoft (http://opensoftdev.com)
 *
 * The unauthorized use of this code outside the boundaries of
 * Opensoft is prohibited.
 *
 * @author Ivetta Tkachenko <ivetta.tkachenko@opensoftdev.ru>
 */

define(['backbone', 'assets/js/modules/utils'], function(backbone, utils) {
    var PricingCalcItemModel;
    PricingCalcItemModel = backbone.Model.extend({
        defaults: {
            /** @var {ProductModel} product */
            product: null,
            /** @var {PricingCalcSkuModel[]} skus */
            skus: [],
            /** @var {PricingCalcOptionModel[]} options */
            options: [],
            /** @var {SideModel[]} sides */
            sides: [],
            /**
             * Selected option names
             * @var {Object} optionSet
             */
            optionSet: {},
            /**
             * Selected sku quantity
             * @var {number} skuId
             */
            quantity: null,
            /**
             * Selected sku sides
             * @var {number} sideCount
             */
            sideCount: null,

            price: 0,
            salePrice: 0,

            currency: null,

            mailingListRecords: 0,
            eventAgent: {}
        },

        /**
         * @returns {PricingCalcSkuModel|undefined}
         */
        getSelectedSku: function() {
            return this.get('skus').findWhere({ selected: true });
        },

        /**
         * @returns {PricingCalcOptionValueModel[]}
         */
        getSelectedOptionValues: function() {
            var selectedOptionValues = [];
            this.get('options').forEach(function(pricingCalcOption) {
                var selectedValue = pricingCalcOption.getSelectedOptionValue();
                if (selectedValue) {
                    selectedOptionValues.push(selectedValue.get('optionValue'));
                }
            });

            return selectedOptionValues;
        },

        /**
         * @param {string} optionName
         * @returns {PricingCalcOptionValueModel|null}
         */
        getSelectedOptionValue: function(optionName) {
            var pricingCalcOption = this.getOption(optionName);
            if (!pricingCalcOption) {
                return null;
            }
            return pricingCalcOption.get('optionValues').findWhere({ selected: true });
        },

        /**
         * @param {string} optionName
         * @returns {string|null}
         */
        getSelectedOptionValueName: function(optionName) {
            var optionValue = this.getSelectedOptionValue(optionName);
            return optionValue ? optionValue.get('optionValue').get('name') : null;
        },

        /**
         * @returns {PricingCalcSideModel|null}
         */
        getSelectedSide: function() {
            return this.get('sides').findWhere({ selected: true });
        },

        /**
         * @param {Object} params
         * @param {boolean} enabledOnly
         * @returns {PricingCalcSkuModel|undefined}
         */
        findSkuBy: function(params, enabledOnly) {
            enabledOnly = enabledOnly || false;
            return this.get('skus').find(function(pricingCalcSku) {
                if (enabledOnly && !pricingCalcSku.get('enabled')) {
                    return false;
                }
                for (var property in params) {
                    if (!(pricingCalcSku.get('sku').get(property) === params[property] && params.hasOwnProperty(property))) {
                        return false;
                    }
                }
                return true;
            });
        },

        /**
         * @param {int} quantity
         * @returns {PricingCalcSkuModel|undefined}
         */
        findSkuWithHigherQuantity: function(quantity) {
            var currentSku = this.getSelectedSku().get('sku');
            return this.get('skus').find(function(pricingCalcSku) {
                if (!pricingCalcSku.get('enabled')) {
                    return false;
                }

                return (
                    pricingCalcSku.get('sku').get('quantity') >= quantity &&
                    currentSku &&
                    pricingCalcSku.get('sku').get('sides') === currentSku.get('sides')
                );
            });
        },

        /**
         * @param {string} optionName
         * @returns {PricingCalcOptionModel|undefined}
         */
        getOption: function(optionName) {
            return this.get('options').find(function(pricingCalcOption) {
                return pricingCalcOption.get('option').get('name') === optionName;
            });
        },

        /**
         * @param {string} optionValueName
         * @param {string} optionName
         * @returns {PricingCalcOptionValueModel|undefined}
         */
        getOptionValue: function(optionValueName, optionName) {
            var pricingCalcOption = this.getOption(optionName);
            if (pricingCalcOption) {
                return pricingCalcOption.get('optionValues').find(function(pricingCalcOptionValue) {
                    return pricingCalcOptionValue.get('optionValue').get('name') === optionValueName;
                });
            }
            return undefined;
        },

        /**
         * @param {number} sideCount
         */
        selectSide: function(sideCount) {
            var oldSide = this.getSelectedSide();
            var side = this.get('sides').findWhere({ sides: sideCount });
            if (side) {
                if (oldSide) {
                    oldSide.set('selected', false);
                }
                side.set('selected', true);
                this.set('sideCount', sideCount);
            }
        },

        /**
         * @param {string} optionValueName
         * @returns {boolean}
         */
        hasOptionValueSelected: function(optionValueName) {
            var targetOption = this.get('options').find(function(pricingCalcOption) {
                var pricingCalcOptionValue = pricingCalcOption.getOptionValue(optionValueName);
                return pricingCalcOptionValue && pricingCalcOptionValue.get('selected');
            });

            return targetOption !== undefined;
        },

        /**
         * @param {string} optionName
         * @returns {boolean}
         */
        hasOption: function(optionName) {
            return this.getOption(optionName) !== undefined;
        },

        /**
         * @param {PricingCalcSkuModel} sku
         */
        findClosestEnabledSku: function(sku) {
            var newSku = this.findSkuBy({ sides: sku.get('sku').get('sides') }, true);
            if (!newSku) {
                newSku = this.findSkuBy({ quantity: sku.get('sku').get('quantity') }, true);
            }
            if (!newSku) {
                newSku = this.get('skus')
                    .where({ enabled: true })
                    .first();
            }
            return newSku;
        },

        /**
         * @param {string} optionValueName
         * @returns {PricingCalcOptionModel|undefined}
         */
        findOptionByOptionValueName: function(optionValueName) {
            var targetOption = this.get('options').find(function(pricingCalcOption) {
                return pricingCalcOption.getOptionValue(optionValueName) !== undefined;
            });

            return targetOption || undefined;
        },

        /**
         * @param {boolean} withVat
         * @returns {number}
         */
        calculateSalePrice: function(withVat) {
            let total = this.getSelectedSku().getSalePrice(withVat);

            this.get('options').each(function(option) {
                const value = option.getSelectedOptionValue();
                if (value) {
                    total += value.getCurrentSalePrice(withVat);
                }
            });

            return total;
        },

        /**
         * @param {boolean} withVat
         * @param {string} [pricingSetName]
         * @return {number}
         */
        calculatePrice: function(withVat) {
            let total = this.getSelectedSku().getPrice(withVat);

            this.get('options').each(function(option) {
                const value = option.getSelectedOptionValue();
                if (value) {
                    total += value.getCurrentPrice(withVat);
                }
            });

            return total;
        }
    });

    return PricingCalcItemModel;
});
